import { datadogRum } from '@datadog/browser-rum';
import { Context as ContextType } from '@datadog/browser-core';
import { datadogLogs } from '@datadog/browser-logs';
import { env } from 'constants/env';
import {
  FullStoryType,
  DataLayerType,
  DatadogType,
  GoogleAnalyticsType,
  SurvicateType,
} from './types';

declare global {
  interface Window {
    FS: any;
    __cc_fullstory_ready?: boolean;
    _cio: any;
    dataLayer: any;
    ga: any;
  }
}

let isFullStoryEnabled = true;
let readyListenerRegistered = false;
const pendingCalls: Array<() => void> = [];

export function resetFullStoryStateForTests() {
  isFullStoryEnabled = true;
  readyListenerRegistered = false;
  pendingCalls.length = 0;
  delete window.__cc_fullstory_ready;
}

// FullStory is loaded via GTM; CaptainCompliance manages consent natively.
// This module provides a typed interaction API over window.FS and queues
// calls made before FullStory is ready.
//
// GTM tag contract — the FullStory GTM tag _fs_ready callback must:
//   1. Set window.__cc_fullstory_ready = true
//   2. Dispatch: window.dispatchEvent(new CustomEvent("fullstory:ready"))

function flushPendingCalls() {
  if (!window.FS) return;
  const calls = pendingCalls.splice(0);
  calls.forEach((call) => call());
}

function ensureReadyListener() {
  if (readyListenerRegistered) return;
  readyListenerRegistered = true;

  window.addEventListener('fullstory:ready', () => flushPendingCalls(), {
    once: true,
  });
}

function whenReady(call: () => void) {
  if (window.__cc_fullstory_ready && window.FS) {
    call();
    return;
  }

  pendingCalls.push(call);
  ensureReadyListener();

  // Re-check: the event may have fired between the first check and the
  // addEventListener call above. The flag persists so we can still flush.
  if (window.__cc_fullstory_ready) {
    flushPendingCalls();
  }
}

export const FullStory: FullStoryType = {
  init: ({ enabled = false }) => {
    isFullStoryEnabled = enabled;
  },
  identify: (id) => {
    if (!isFullStoryEnabled) return;
    whenReady(() => {
      if (window.FS?.identify) window.FS.identify(id);
    });
  },
  setUserVars: (properties) => {
    if (!isFullStoryEnabled) return;
    whenReady(() => {
      if (window.FS?.setUserVars) window.FS.setUserVars(properties);
    });
  },
  setPageVars: (properties) => {
    if (!isFullStoryEnabled) return;
    whenReady(() => {
      if (!window.FS?.setVars) return;
      // The setVars method belongs to the FullStory V1 API.
      // To verify the API version, run FS._v in the browser console.
      window.FS.setVars('page', properties);
    });
  },
  onReady: (callback) => {
    if (!isFullStoryEnabled) return;
    whenReady(() => {
      if (window.FS) callback(window.FS);
    });
  },
  getCurrentSession: () => {
    if (!isFullStoryEnabled || !window.FS) return null;
    if (typeof window.FS.getCurrentSession !== 'function') return null;

    return window.FS.getCurrentSession();
  },
  track: (event, properties = {}) => {
    if (!isFullStoryEnabled) return;
    whenReady(() => {
      if (window.FS) {
        window.FS('trackEvent', {
          name: event,
          properties: properties,
        });
      }
    });
  },
};

const isEnabled = env.nodeEnv !== 'development';

export const DataLayer: DataLayerType = {
  push: (properties) => {
    if (!isEnabled || !window.dataLayer || !window.dataLayer.push) return;
    window.dataLayer.push(properties);
  },
};

export const GoogleAnalytics: GoogleAnalyticsType = {
  send: (event, location) => {
    if (!isEnabled || !window.ga) return;
    window.ga('send', event, location);
  },
};

export const Datadog: DatadogType = {
  /*
    Add additional Datadog context:
    https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#add-global-context
    Datadog naming convention for a better correlation of data:
    https://docs.datadoghq.com/logs/processing/attributes_naming_convention/#user-related-attributes
  */
  addUserContext: ({ appId, clickId }) => {
    datadogRum.setGlobalContextProperty('usr', {
      app_id: appId,
      click_id: clickId,
    } as ContextType);
  },
  identify: (id) => {
    datadogRum.setUser({ id });
  },
  logEvent: ({ level, message, messageContext }) => {
    const config = {
      ...messageContext,
      service: 'acquisition-app',
    };
    datadogLogs.logger[level](message, config);
  },
  logInfo: (eventName, eventAttributes) => {
    const config = {
      ...eventAttributes,
      service: 'acquisition-app',
    };
    datadogLogs.logger.info(eventName, config);
  },
};

export const Survicate: SurvicateType = {
  setVisitorTraits: function (properties = {}) {
    if (!window._sva) return;

    window._sva.setVisitorTraits({
      ...properties,
    });
  },
  invokeEvent: function (eventName) {
    if (!window._sva) return;

    window._sva.invokeEvent(eventName);
  },
};
